import { formatLastEdited, escapeHtml } from '../utils/index.js';

class NoteDetail extends HTMLElement {
  _note = null;
  _boundHandlers = {};

  static observedAttributes = ['view'];

  constructor() {
    super();
  }

  set note(value) {
    this._note = value;
    this.render();
  }

  get note() {
    return this._note;
  }

  get view() {
    return this.getAttribute('view') || 'desktop';
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    this._removeEventListeners();
  }

  attributeChangedCallback() {
    this.render();
  }

  _removeEventListeners() {
    const backBtn = this.querySelector('.note-detail-back-btn');
    const archiveBtn = this.querySelector('.note-detail-archive-btn');
    const deleteBtn = this.querySelector('.note-detail-delete-btn');

    if (backBtn && this._boundHandlers.backClick) {
      backBtn.removeEventListener('click', this._boundHandlers.backClick);
    }
    if (archiveBtn && this._boundHandlers.archiveClick) {
      archiveBtn.removeEventListener('click', this._boundHandlers.archiveClick);
    }
    if (deleteBtn && this._boundHandlers.deleteClick) {
      deleteBtn.removeEventListener('click', this._boundHandlers.deleteClick);
    }
  }

  _attachEventListeners() {
    const backBtn = this.querySelector('.note-detail-back-btn');
    const archiveBtn = this.querySelector('.note-detail-archive-btn');
    const deleteBtn = this.querySelector('.note-detail-delete-btn');

    this._boundHandlers.backClick = () => {
      this.dispatchEvent(
        new CustomEvent('back-pressed', {
          bubbles: true,
        })
      );
    };

    this._boundHandlers.archiveClick = () => {
      const eventName = this._note.archived ? 'note-unarchived' : 'note-archived';
      this.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { id: this._note.id },
          bubbles: true,
        })
      );
    };

    this._boundHandlers.deleteClick = () => {
      if (confirm('Are you sure you want to delete this note?')) {
        this.dispatchEvent(
          new CustomEvent('note-deleted', {
            detail: { id: this._note.id },
            bubbles: true,
          })
        );
      }
    };

    backBtn?.addEventListener('click', this._boundHandlers.backClick);
    archiveBtn?.addEventListener('click', this._boundHandlers.archiveClick);
    deleteBtn?.addEventListener('click', this._boundHandlers.deleteClick);
  }

  render() {
    this._removeEventListeners();

    const isMobile = this.view === 'mobile';
    const mobileClass = isMobile ? 'mobile' : '';

    if (!this._note) {
      this.innerHTML = `
        <div class="note-detail-empty" role="status">
          <h3 class="note-detail-empty-title">Select a Note</h3>
          <p class="note-detail-empty-text">Choose a note from the list to view</p>
        </div>
      `;
      return;
    }

    const archiveIcon = this._note.archived ? 'unarchive' : 'archive';
    const archiveTitle = this._note.archived ? 'Unarchive' : 'Archive';
    const escapedTitle = escapeHtml(this._note.title);
    const escapedBody = escapeHtml(this._note.body);
    const formattedDate = formatLastEdited(this._note.createdAt);

    this.innerHTML = `
      <div class="note-detail-container">
        <header class="note-detail-header ${mobileClass}">
          <button class="note-detail-back-btn" title="Back" aria-label="Go back to notes list">
            <span class="material-symbols-outlined" aria-hidden="true">arrow_back</span>
          </button>
          <time class="note-detail-last-edited" datetime="${this._note.createdAt}">${formattedDate}</time>
          <div class="note-detail-actions" role="toolbar" aria-label="Note actions">
            <button class="note-detail-action-btn note-detail-archive-btn" title="${archiveTitle}" aria-label="${archiveTitle} this note">
              <span class="material-symbols-outlined" aria-hidden="true">${archiveIcon}</span>
            </button>
            <button class="note-detail-action-btn delete note-detail-delete-btn" title="Delete" aria-label="Delete this note">
              <span class="material-symbols-outlined" aria-hidden="true">delete</span>
            </button>
          </div>
        </header>
        <article class="note-detail-content ${mobileClass}">
          <h1 class="note-detail-title ${mobileClass}">${escapedTitle}</h1>
          <p class="note-detail-body">${escapedBody}</p>
        </article>
      </div>
    `;

    this._attachEventListeners();
  }
}

customElements.define('note-detail', NoteDetail);
